/**
 * PhoneCleanupCard — Telefonnummern vereinheitlichen.
 *
 * Bringt alle gespeicherten Nummern auf ein einheitliches Format (ohne
 * Leerzeichen), damit Suche + WhatsApp-Zuordnung zuverlässig funktionieren
 * und keine Doppel-Konten entstehen. Erst Vorschau (dry_run), dann bereinigen.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Phone, Eye, Wand2, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PhoneCleanupCard = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async (dryRun) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/normalize-phones?dry_run=${dryRun}`, {}, auth());
      setPreview({ ...r.data, applied: !dryRun });
      if (dryRun) {
        toast.success(`Vorschau: ${r.data.customers_normalized} Nummer(n) würden vereinheitlicht`);
      } else {
        toast.success(`Bereinigt: ${r.data.customers_normalized} Kunden + ${r.data.users_normalized} Konten`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler bei der Bereinigung');
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <>
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => run(true)} disabled={loading} data-testid="phone-cleanup-preview-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
          Vorschau
        </Button>
        <Button
          onClick={() => run(false)}
          disabled={loading || !preview}
          data-testid="phone-cleanup-apply-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
          Jetzt bereinigen
        </Button>
      </div>

      {preview && (
        <div className="rounded-lg border p-4 bg-gray-50 space-y-3" data-testid="phone-cleanup-result">
          <p className="text-sm font-medium text-gray-900">
            {preview.applied ? '✅ Bereinigt' : '👁️ Vorschau'}: {preview.customers_normalized} Kundennummer(n)
            {preview.applied ? ` + ${preview.users_normalized} Konten` : ''} betroffen.
          </p>

          {(preview.changes || []).length > 0 && (
            <div className="max-h-56 overflow-y-auto text-sm space-y-1" data-testid="phone-cleanup-changes">
              {preview.changes.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 border-b border-gray-200 py-1">
                  <span className="text-gray-700">{c.name || '—'}</span>
                  <span className="text-gray-500">
                    <span className="line-through mr-2">{c.old}</span>
                    <span className="font-semibold text-gray-900">{c.new}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {(preview.duplicate_numbers || []).length > 0 && (
            <div className="text-sm">
              <p className="flex items-center gap-2 font-medium text-amber-700 mb-1">
                <Users className="h-4 w-4" /> Nummern mit mehreren Kunden ({preview.duplicate_numbers.length})
              </p>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {preview.duplicate_numbers.map((d) => (
                  <div key={d.phone} className="border border-amber-200 bg-amber-50 rounded p-2">
                    <p className="font-semibold text-amber-900">{d.phone} ({d.count})</p>
                    {(d.customers || []).map((x) => (
                      <p key={x.id} className="text-xs text-amber-800">
                        {x.name} · {x.email}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Hinweis: Test-Nummern können absichtlich mehrfach vorkommen. Für echte Doppel-Konten sag mir Bescheid.
              </p>
            </div>
          )}

          {!preview.applied && (preview.customers_normalized > 0) && (
            <p className="text-xs text-gray-600">Klicke „Jetzt bereinigen", um die Änderungen zu übernehmen.</p>
          )}
          {!preview.applied && preview.customers_normalized === 0 && (
            <p className="text-xs text-green-700">Alle Nummern sind bereits einheitlich – nichts zu tun. ✅</p>
          )}
        </div>
      )}
    </>
  );

  // Eingebetteter Modus (z.B. im Werkzeuge-Bereich der Kundenverwaltung):
  // ohne eigene Card-Hülle, nur Titel + Inhalt.
  if (embedded) {
    return (
      <div className="space-y-4" data-testid="phone-cleanup-card">
        <div>
          <p className="flex items-center gap-2 font-semibold text-gray-900">
            <Phone className="h-5 w-5" /> Telefonnummern bereinigen
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Vereinheitlicht alle Nummern (entfernt Leerzeichen) → zuverlässige Suche &amp; WhatsApp-Zuordnung, keine Doppel-Konten. Erst Vorschau, dann bereinigen.
          </p>
        </div>
        {body}
      </div>
    );
  }

  return (
    <Card data-testid="phone-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" /> Telefonnummern bereinigen
        </CardTitle>
        <CardDescription>
          Vereinheitlicht alle Nummern (entfernt Leerzeichen) → zuverlässige Suche &amp; WhatsApp-Zuordnung, keine Doppel-Konten. Erst Vorschau, dann bereinigen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {body}
      </CardContent>
    </Card>
  );
};

export default PhoneCleanupCard;
